import {
  alterationMessagesSchema,
  alterationPageSchema,
  alterationPaymentResultSchema,
  alterationPaymentsSchema,
  alterationSchema,
  messageLogEntrySchema,
  orderMessagePreviewSchema,
  type Alteration,
  type AlterationCollectInput,
  type AlterationMessages,
  type AlterationPage,
  type AlterationPaymentResult,
  type AlterationPayments,
  type AlterationStatus,
  type MessageLogEntry,
  type NotificationEvent,
  type OrderMessagePreview,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/** `/alterations` — the work that came back. */

const ALTERATIONS = '/alterations';

export interface AlterationListParams {
  status?: AlterationStatus;
  openOnly?: boolean;
  customerId?: string;
  assignedToUserId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export async function getAlterations(
  client: ApiClient,
  params: AlterationListParams = {},
): Promise<AlterationPage> {
  return await client.get(ALTERATIONS, {
    schema: alterationPageSchema,
    query: {
      ...(params.status ? { status: params.status } : {}),
      ...(params.openOnly ? { openOnly: 'true' } : {}),
      ...(params.customerId ? { customerId: params.customerId } : {}),
      ...(params.assignedToUserId ? { assignedToUserId: params.assignedToUserId } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.page ? { page: params.page } : {}),
      ...(params.pageSize ? { pageSize: params.pageSize } : {}),
    },
  });
}

export async function createAlteration(
  client: ApiClient,
  body: Record<string, unknown>,
): Promise<Alteration> {
  return await client.post(ALTERATIONS, body, { schema: alterationSchema });
}

export async function getAlteration(client: ApiClient, alterationId: string): Promise<Alteration> {
  return await client.get(`${ALTERATIONS}/${alterationId}`, { schema: alterationSchema });
}

export async function moveAlteration(
  client: ApiClient,
  alterationId: string,
  body: {
    toStatus: AlterationStatus;
    reason?: string | null;
    /** With `delivered` only: the balance, taken at the hand-over. */
    collect?: AlterationCollectInput | null;
  },
): Promise<Alteration> {
  return await client.post(`${ALTERATIONS}/${alterationId}/status`, body, {
    schema: alterationSchema,
  });
}

// --- Money ------------------------------------------------------------------------------------

/** Where a payment on an alteration is POSTed — the record dialog's `paymentsPath`. */
export function alterationPaymentsPath(alterationId: string): string {
  return `${ALTERATIONS}/${alterationId}/payments`;
}

export async function getAlterationPayments(
  client: ApiClient,
  alterationId: string,
): Promise<AlterationPayments> {
  return await client.get(alterationPaymentsPath(alterationId), {
    schema: alterationPaymentsSchema,
  });
}

export async function reverseAlterationPayment(
  client: ApiClient,
  alterationId: string,
  paymentId: string,
  reason: string,
): Promise<AlterationPaymentResult> {
  return await client.post(
    `${alterationPaymentsPath(alterationId)}/${paymentId}/reverse`,
    { reason },
    { schema: alterationPaymentResultSchema },
  );
}

// --- Messages ---------------------------------------------------------------------------------

export async function getAlterationMessages(
  client: ApiClient,
  alterationId: string,
): Promise<AlterationMessages> {
  return await client.get(`${ALTERATIONS}/${alterationId}/messages`, {
    schema: alterationMessagesSchema,
  });
}

export async function previewAlterationMessage(
  client: ApiClient,
  alterationId: string,
  event: NotificationEvent,
): Promise<OrderMessagePreview> {
  return await client.get(`${ALTERATIONS}/${alterationId}/messages/preview?event=${event}`, {
    schema: orderMessagePreviewSchema,
  });
}

export async function sendAlterationMessage(
  client: ApiClient,
  alterationId: string,
  event: NotificationEvent,
): Promise<MessageLogEntry> {
  return await client.post(
    `${ALTERATIONS}/${alterationId}/messages`,
    { event },
    { schema: messageLogEntrySchema },
  );
}
